import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import usePlayerManifest from "../hooks/usePlayerManifest";
import PlayerLayout from "../components/PlayerLayout";

const ITEMS = [
  {
    icon: "🏌️", title: "Declare Your Marker",
    body: "At the first tee, agree with your group who's marking who — same as real golf. Use this to record it, so the right person gets asked to verify your card afterwards."
  },
  {
    icon: "📋", title: "Verify a Playing Partner",
    body: "After your round, confirm a playing partner's scorecard here. Your organiser must have assigned you as their marker before the round for this to work."
  },
  {
    icon: "📊", title: "My Results",
    body: "Your personal results and verification status across every competition your organiser has archived."
  },
];

export default function PlayerDashboard() {
  usePlayerManifest();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = sessionStorage.getItem("player_token");
  const name = sessionStorage.getItem("player_name");
  const unlinked = searchParams.get("notice") === "unlinked";

  const [nextEvent, setNextEvent] = useState(null);
  const [myResponse, setMyResponse] = useState(null);
  const [responding, setResponding] = useState(false);

  const loadNextEvent = () => {
    fetch("/api/planner/mine/next", { headers: { Authorization: "Bearer " + token } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) { setNextEvent(data.fixture); setMyResponse(data.myResponse); } })
      .catch(e => console.error(e));
  };

  useEffect(() => {
    if (!token) { navigate("/player/login"); return; }
    fetch("/api/player/dashboard", { headers: { Authorization: "Bearer " + token } })
      .then(r => { if (!r.ok) { navigate("/player/login"); return null; } return r.json(); })
      .then(data => { if (data) setPlayer(data.player); })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
    loadNextEvent();
  }, []);

  const respondToNextEvent = (choice, buggy) => {
    if (!nextEvent) return;
    setResponding(true);
    fetch(`/api/planner/${nextEvent.id}/mine`, {
      method: "POST",
      headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
      body: JSON.stringify({ choice, buggy: !!buggy }),
    })
      .then(r => r.json())
      .then(() => loadNextEvent())
      .catch(e => console.error(e))
      .finally(() => setResponding(false));
  };

  const isPinHigh = player?.society_id === 7;
  const visibleItems = ITEMS.filter(item => !item.pinHighOnly || isPinHigh);

  const instructions = (
    <>
      <h2 style={{ fontSize: "14px", fontWeight: "700", color: "#14532d", marginBottom: "8px" }}>Marking &amp; verifying, step by step</h2>
      <div style={{ fontSize: "12px", color: "#374151", lineHeight: "2.2" }}>
        1. At the first tee, agree who's marking who<br/>
        2. Tap <strong>Declare Your Marker</strong> in the sidebar to record it<br/>
        3. During the round, mark that player's card as normal<br/>
        4. Afterwards, tap <strong>Verify a Playing Partner</strong><br/>
        5. Select the date and confirm the score<br/>
        6. A <strong>✅ Verified</strong> badge appears next to their score on the results page
      </div>
    </>
  );

  return (
    <PlayerLayout instructions={instructions}>
      <h1 style={{ fontSize: "20px", fontWeight: "800", color: "#14532d", marginBottom: "4px" }}>Welcome{name ? ", " + name.split(" ")[0] : ""}</h1>
      <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "20px" }}>What each item in the sidebar does.</p>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>Loading...</div>
      ) : (
        <>
          {unlinked && (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "10px", padding: "14px 16px", marginBottom: "16px", fontSize: "13px", color: "#166534" }}>
              <strong>✅ Registration successful!</strong><br/>
              Your account has been created. Your society organiser needs to add you to the player list. Once added, log in again and your account will link automatically.
            </div>
          )}

          {nextEvent && (
            <div style={{ background: "white", borderRadius: "10px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "18px 20px", marginBottom: "16px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "4px" }}>Next Event</div>
              <div style={{ fontSize: "16px", fontWeight: 800, color: "#14532d" }}>{nextEvent.event_name}</div>
              <div style={{ fontSize: "12.5px", color: "#6b7280", marginBottom: "14px" }}>
                {new Date(nextEvent.event_date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })} · {nextEvent.venue}
                {nextEvent.closing_date && <> · reply by {new Date(nextEvent.closing_date).toLocaleDateString("en-GB")}</>}
              </div>

              {myResponse && myResponse.attendance_status !== "no_response" ? (
                <div style={{ fontSize: "13px", color: myResponse.attendance_status === "reserve" ? "#92400e" : "#166534", fontWeight: 700 }}>
                  {myResponse.attendance_status === "reserve" ? `🕒 You're on the reserve list (#${myResponse.reserve_position})` :
                   myResponse.attendance_status === "declined" ? "You said you can't make it" :
                   `✅ You're signed up${myResponse.buggy ? " — 🚗 buggy requested" : ""}`}
                  {nextEvent.effective_status === "open" ? (
                    <div style={{ marginTop: "8px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {myResponse.attendance_status === "declined" ? (
                        <>
                          <button onClick={() => respondToNextEvent("signup", false)} disabled={responding}
                            style={{ background: "#166534", color: "white", border: "none", borderRadius: "8px", padding: "6px 14px", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>
                            Sign Up
                          </button>
                          <button onClick={() => respondToNextEvent("signup", true)} disabled={responding}
                            style={{ background: "#1d4ed8", color: "white", border: "none", borderRadius: "8px", padding: "6px 14px", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>
                            Buggy Required
                          </button>
                        </>
                      ) : (
                        <>
                          {!myResponse.buggy && (
                            <button onClick={() => respondToNextEvent("signup", true)} disabled={responding}
                              style={{ background: "#1d4ed8", color: "white", border: "none", borderRadius: "8px", padding: "6px 14px", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>
                              + Add Buggy
                            </button>
                          )}
                          <button onClick={() => respondToNextEvent("decline")} disabled={responding}
                            style={{ background: "none", border: "1px solid #d1d5db", borderRadius: "8px", padding: "6px 14px", fontSize: "12px", color: "#6b7280", cursor: "pointer" }}>
                            Can't make it
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    <div style={{ marginTop: "8px", fontSize: "11.5px", color: "#9ca3af", fontWeight: 400 }}>Signups have closed — contact your organiser to change your answer.</div>
                  )}
                </div>
              ) : nextEvent.effective_status === "open" ? (
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button disabled={responding} onClick={() => respondToNextEvent("signup", false)}
                    style={{ background: "#166534", color: "white", border: "none", borderRadius: "8px", padding: "8px 14px", fontSize: "12.5px", fontWeight: 700, cursor: "pointer", opacity: responding ? 0.6 : 1 }}>
                    Sign Up
                  </button>
                  <button disabled={responding} onClick={() => respondToNextEvent("signup", true)}
                    style={{ background: "#1d4ed8", color: "white", border: "none", borderRadius: "8px", padding: "8px 14px", fontSize: "12.5px", fontWeight: 700, cursor: "pointer", opacity: responding ? 0.6 : 1 }}>
                    Buggy Required
                  </button>
                </div>
              ) : (
                <div style={{ fontSize: "12.5px", color: "#9ca3af" }}>Signups have closed for this event.</div>
              )}
            </div>
          )}

          <div style={{ background: "white", borderRadius: "10px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" }}>
            {visibleItems.map((item, i) => (
              <div key={item.title} style={{ display: "flex", gap: "14px", padding: "18px 20px", borderBottom: i < visibleItems.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                <div style={{ fontSize: "22px", flexShrink: 0, lineHeight: 1.4 }}>{item.icon}</div>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#14532d", marginBottom: "4px" }}>{item.title}</div>
                  <div style={{ fontSize: "12.5px", color: "#6b7280", lineHeight: "1.6" }}>{item.body}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </PlayerLayout>
  );
}
